import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    FormControlLabel,
    FormGroup,
    Radio,
    RadioGroup,
    Stack,
    SxProps,
    Typography,
} from '@mui/material';
import { bodyFont, labelFontSemiBold, vars } from '../../../../../Theme';
import { useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../../redux/store';
import SelectWorkflowOption from './SelectWorkflowOption';
import {
    InitialRelaseState,
    StateChangedArgumentsInitialRelease,
} from '../InitialReleaseDialog/InitialReleaseDialog';
import SelectWorkflowNestedOption from './SelectWorkflowNestedOption';
import OptionsTitle from './OptionsTitle';
import { mdiAlertCircle } from '@mdi/js';
import Message from '../../_parts/Message';

const taskpathLabelSx: SxProps = {
    alignItems: 'flex-start',
    mr: 0,
    ml: 0,
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    flexGrow: 4,
    '& .MuiFormControlLabel-label': {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        alignSelf: 'center',
    },
    '& .MuiFormControlLabel-root': {
        mr: 0,
    },
    '& .MuiButtonBase-root ': {
        p: '6px',
    },
};

const roleLabelSx: SxProps = {
    ml: 0,
    ' span': {
        fontFamily: labelFontSemiBold,
        fontSize: '16px',
    },
};

const groupedRadiosTitleSx: SxProps = {
    fontFamily: bodyFont,
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'normal',
    pl: '12px',
};

const radioGroupSx: SxProps = {
    ml: '36px',
};

const errorText = (text: string) => {
    return (
        <Message
            text={text}
            icon={mdiAlertCircle}
            color={vars.errorDialogRed}
            typographySx={{ fontWeight: 700 }}
            wrapperSx={{
                border: 'none',
                padding: '2px',
                mb: 1,
            }}
        />
    );
};

export const SelectWorkflow = ({
    parentValue,
    getStateChanged,
    selected,
    hasError,
}: ISelectWorkflowProps) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'initialReleaseDialog' });

    const selectedTab = useSelector(
        (_store: IAppReduxStore) => _store.projectWorkflow.selectedReleaseTab,
    );
    const options = useSelector(
        (_store: IAppReduxStore) => _store.projectWorkflow.releaseOptionsData[selectedTab].options,
    );

    const exclusiveSelectComputed = useMemo(
        () => selected?.[parentValue] ?? '',
        [selected?.[parentValue]],
    );

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>, option) => {
        const { value } = event.target;

        getStateChanged({
            parentValue: parentValue,
            state:
                option.workflowTask?.length === 1
                    ? options.find(optionItem => optionItem.path === value)?.workflowTask?.[0]?.path
                    : value,
        });
    };

    const showErrorConditionTopLevel = () => {
        return hasError && !exclusiveSelectComputed.length;
    };

    const showErrorConditionNestedLevel = option => {
        return hasError && exclusiveSelectComputed.includes(option.path);
    };

    return (
        <>
            <OptionsTitle title={t('selectWorkflowTitle')} />
            {showErrorConditionTopLevel() && errorText(t('errorMessage'))}
            <FormGroup>
                {options
                    .slice()
                    .sort((a, b) => a.workflowTitle.localeCompare(b.workflowTitle))
                    .map(option => {
                        if (!option.workflowTask?.length) {
                            return (
                                <SelectWorkflowOption
                                    key={`${option.workflowId}`}
                                    option={option}
                                    checked={exclusiveSelectComputed.includes(option.path)}
                                    handleChange={e => {
                                        handleChange(e, option);
                                    }}
                                    hasError={showErrorConditionTopLevel()}
                                />
                            );
                        } else {
                            return (
                                <SelectWorkflowOption
                                    key={`${option.workflowId}`}
                                    option={option}
                                    checked={exclusiveSelectComputed.includes(option.path)}
                                    handleChange={e => {
                                        handleChange(e, option);
                                    }}
                                    hasError={
                                        showErrorConditionTopLevel() ||
                                        showErrorConditionNestedLevel(option)
                                    }
                                >
                                    {exclusiveSelectComputed.includes(option.path) && (
                                        <RadioGroup
                                            aria-labelledby='initial-workflow-select-release-path'
                                            value={selected}
                                            onChange={e => {
                                                handleChange(e, option);
                                            }}
                                            sx={radioGroupSx}
                                        >
                                            {showErrorConditionNestedLevel(option) && (
                                                <Box sx={{ pl: '8px' }}>
                                                    {errorText(t('errorMessage'))}
                                                </Box>
                                            )}
                                            <Typography sx={groupedRadiosTitleSx}>
                                                {t('chooseStartingTask')}
                                            </Typography>
                                            {option.workflowTask.map(task =>
                                                Array.isArray(task.role) ? (
                                                    <Box key={task.path}>
                                                        <FormControlLabel
                                                            key={task.path}
                                                            sx={taskpathLabelSx}
                                                            value={task.path}
                                                            control={
                                                                <Radio
                                                                    checked={
                                                                        exclusiveSelectComputed?.includes(
                                                                            task.path,
                                                                        ) ?? false
                                                                    }
                                                                    value={task.path}
                                                                    onChange={e => {
                                                                        handleChange(e, task);
                                                                    }}
                                                                    name={task.path}
                                                                />
                                                            }
                                                            label={task.workflowTaskTitle}
                                                        />
                                                        <RadioGroup
                                                            aria-labelledby='initial-workflow-select-release-path'
                                                            value={selected}
                                                            onChange={e => {
                                                                handleChange(e, task.role);
                                                            }}
                                                            sx={radioGroupSx}
                                                        >
                                                            {task.role.map(role => (
                                                                <FormControlLabel
                                                                    key={role.path}
                                                                    value={role.path}
                                                                    checked={exclusiveSelectComputed.includes(
                                                                        role.path,
                                                                    )}
                                                                    control={
                                                                        <Radio
                                                                            sx={{
                                                                                padding: '8px 12px',
                                                                            }}
                                                                        />
                                                                    }
                                                                    label={
                                                                        <Stack
                                                                            direction='row'
                                                                            sx={{ ml: 0 }}
                                                                        >
                                                                            <Stack sx={{ ml: 0 }}>
                                                                                <Typography
                                                                                    sx={{
                                                                                        ...labelFontSemiBold,
                                                                                        color: vars.font,
                                                                                    }}
                                                                                >
                                                                                    {role.role}
                                                                                </Typography>
                                                                            </Stack>
                                                                        </Stack>
                                                                    }
                                                                    sx={roleLabelSx}
                                                                />
                                                            ))}
                                                        </RadioGroup>
                                                    </Box>
                                                ) : (
                                                    <SelectWorkflowNestedOption
                                                        key={task.workflowTaskId}
                                                        task={task}
                                                        exclusiveSelectComputed={
                                                            exclusiveSelectComputed
                                                        }
                                                    />
                                                ),
                                            )}
                                        </RadioGroup>
                                    )}
                                </SelectWorkflowOption>
                            );
                        }
                    })}
            </FormGroup>
        </>
    );
};

interface ISelectWorkflowProps {
    parentValue: string;
    selected: InitialRelaseState;
    getStateChanged: (arg: StateChangedArgumentsInitialRelease) => void;
    hasError?: boolean;
}
